import React, { useRef, useState, useEffect, useMemo } from "react";
import JoditEditor from "jodit-react";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../hooks/useAppDispatch.tsx";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store.tsx";
import { updateContent } from "../../../redux/features/contentManagement/contentSlice.tsx";

const editorStyles = `
  .jodit-container ul {
    
    padding-left: 40px !important;
  }
  .jodit-container ol {
    
    padding-left: 40px !important;
  }
  .jodit-wysiwyg ul li {
    
  }
  .jodit-wysiwyg ol li {
    
  }
`;

type RichTextEditorProps = {
  onCancel?: () => void;
  onSaveSuccess?: () => void;
  placeholder?: string;
  value: string;
  type: number;
  lang: string; 
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  onCancel,
  onSaveSuccess,
  value,
  placeholder = "Enter details",
  type,
  lang = 'en',
}) => {
  const editor = useRef(null);
  const [content, setContent] = useState("");
  const [initialContent, setInitialContent] = useState("");
  const [editorPlaceholder, setEditorPlaceholder] = useState(placeholder);
  const dispatch = useAppDispatch();
  const { isLoading } = useSelector((state: RootState) => state.content);

  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = editorStyles;
    document.head.appendChild(styleEl);

    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  useEffect(() => {
    if (value) {
      setContent(value);
      setInitialContent(value);
      setEditorPlaceholder(""); // Remove placeholder when there's content
    } else {
      setEditorPlaceholder(placeholder); // Show placeholder when there's no content
    }
  }, [value, placeholder]);

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: editorPlaceholder,
      enableDragAndDropFileToEditor: false,
    }),
    [editorPlaceholder]
  );

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    // Update placeholder based on content
    setEditorPlaceholder(newContent ? "" : placeholder);
  };

  const handleSave = async () => {
    try {
      const resultAction = await dispatch(
        updateContent({ type: type, content: content, lang }) // Add lang here
      );

      if (
        updateContent.fulfilled.match(resultAction) &&
        resultAction.payload.statusCode === 200
      ) {
        toast.success("Content updated successfully");
        setInitialContent(content);
        onSaveSuccess?.();
      }
    } catch (error) {
      toast.error("Failed to update content");
      console.error("Error saving content:", error);
    }
  };

  const isContentChanged = content !== initialContent;

  return (
    <div className="w-full bg-white">
      <JoditEditor
        ref={editor}
        value={content}
        config={config}
        onChange={handleContentChange}
      />

      {onSaveSuccess && (
        <div className="flex justify-end gap-2">
          <Button
            variant="outlined"
            onClick={onCancel}
            style={{
              fontSize: "16px",
              fontWeight: "400",
              marginTop: "10px",
              padding: "8px 16px",
              color: "#111",
              border: "1px solid #C4C4C4",
              borderRadius: "10px",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isContentChanged || isLoading}
            style={{
              fontSize: "16px",
              fontWeight: "700",
              marginTop: "10px",
              padding: "8px 16px",
              background:
                isContentChanged && !isLoading ? "#3BC1CD" : "#C4C4C4",
              color: "#FFF",
              border: "none",
              borderRadius: "8px",
              cursor:
                isContentChanged && !isLoading ? "pointer" : "not-allowed",
            }}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
